// CanvasApprovals — Ship 8 (Apr 30, 2026)
// Manager-tier approval queue for canvasser-submitted appointments.
// Lists pending_canvas_appointments where status='pending', shows the homeowner +
// appointment details + a snippet of context, lets Alex/manager approve or reject.
// Approve calls canvas-approve-appointment edge function which creates the deal +
// flips the pin to appt_scheduled + auto-fires storm-report-prepare.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase, SUPABASE_URL } from "../lib/supabase";
import { fetchEdge } from "../lib/edgeError";

interface PendingRow {
  id: string;
  knock_pin_id: string | null;
  submitted_by: string;
  submitted_at: string;
  homeowner_first: string;
  homeowner_last: string;
  homeowner_phone: string;
  homeowner_email: string | null;
  property_street: string;
  property_city: string;
  property_state: string;
  property_zip: string | null;
  insurance_company: string | null;
  roof_age_estimate: number | null;
  sms_consent: boolean;
  appointment_at: string;
  notes: string | null;
  status: string;
  rejection_reason: string | null;
  approved_at: string | null;
  deal_id: string | null;
  // Ship 6: where the rep was when they submitted
  submitter_lat: number | null;
  submitter_lon: number | null;
  // joined
  submitter?: { full_name: string } | null;
  pin?: { lat: number | null; lon: number | null } | null;
}

// Haversine distance in feet between two GPS coords. Returns null if either
// coord is missing.
function distanceFeet(
  aLat: number | null | undefined,
  aLon: number | null | undefined,
  bLat: number | null | undefined,
  bLon: number | null | undefined,
): number | null {
  if (aLat == null || aLon == null || bLat == null || bLon == null) return null;
  const R = 6371000; // meters
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const sa =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  const meters = 2 * R * Math.asin(Math.min(1, Math.sqrt(sa)));
  return meters * 3.28084;
}

// Map distance → chip style. Aligned with the spec:
//   ≤ 50 ft  🟢 On property — default-accept signal
//   ≤ 300 ft 🟡 Near        — at the curb / driveway / neighbor
//   > 300 ft 🔴 Far          — submitted from somewhere else
function distanceChip(ft: number | null): {
  color: string; bg: string; label: string; emoji: string;
} | null {
  if (ft == null) return null;
  if (ft <= 50)  return { emoji: "🟢", label: "On property",   bg: "bg-emerald-100", color: "text-emerald-800" };
  if (ft <= 300) return { emoji: "🟡", label: `${Math.round(ft)} ft`, bg: "bg-amber-100",   color: "text-amber-800"   };
  if (ft <= 5280) return { emoji: "🔴", label: `${Math.round(ft)} ft`, bg: "bg-red-100",     color: "text-red-800"     };
  return            { emoji: "🔴", label: `${(ft/5280).toFixed(1)} mi`, bg: "bg-red-100", color: "text-red-800" };
}

const MANAGER_ROLES = new Set([
  "admin","sales_director","asm","team_lead",
  "restore_manager","roof_team_manager","roof_director","director_of_operations",
]);

type Filter = "pending" | "approved" | "rejected" | "all";

// Inner queue (filter pills + list + approve/reject). Extracted Jun 16, 2026
// so the new combined ASM board (/asm) can embed the same queue without
// duplicating logic. The default export below wraps this with page chrome.
export function CanvasApprovalsInner() {
  const { role, isSuperUser } = useAuth();
  const [filter, setFilter] = useState<Filter>("pending");
  const [rows, setRows] = useState<PendingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);

  const isManager = useMemo(
    () => isSuperUser || (role && MANAGER_ROLES.has(role)),
    [role, isSuperUser]
  );

  async function load() {
    setLoading(true);
    let q = supabase
      .from("pending_canvas_appointments")
      .select("*, submitter:team_roster!pending_canvas_appointments_submitted_by_fkey(full_name), pin:knock_pins!pending_canvas_appointments_knock_pin_id_fkey(lat, lon)")
      .order("submitted_at", { ascending: false })
      .limit(100);
    if (filter !== "all") q = q.eq("status", filter);
    const { data, error } = await q;
    if (error) setErr(error.message);
    setRows((data ?? []) as any as PendingRow[]);
    setLoading(false);
  }

  useEffect(() => { load(); }, [filter]);

  async function approve(row: PendingRow) {
    if (!confirm(`Approve appointment for ${row.homeowner_first} ${row.homeowner_last} at ${row.property_street}?\n\nA new deal will be created and the storm report will auto-generate.`)) return;
    setWorking(row.id);
    setErr(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      const resp = await fetchEdge(
        `${SUPABASE_URL}/functions/v1/canvas-approve-appointment`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ pending_id: row.id, action: "approve" }),
        }
      );
      const body = await resp.json();
      if (!resp.ok || !body.ok) throw new Error(body.error ?? `HTTP ${resp.status}`);
      // Open the new deal in a new tab
      if (body.deal_id) window.open(`/deal/${body.deal_id}`, "_blank");
      await load();
    } catch (e: any) {
      setErr(e.message ?? String(e));
    } finally {
      setWorking(null);
    }
  }

  async function reject(row: PendingRow) {
    const reason = window.prompt(`Reject appointment for ${row.homeowner_first} ${row.homeowner_last}?\nReason (visible to canvasser):`);
    if (reason === null) return;   // cancelled
    setWorking(row.id);
    setErr(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session.session?.access_token;
      const resp = await fetchEdge(
        `${SUPABASE_URL}/functions/v1/canvas-approve-appointment`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ pending_id: row.id, action: "reject", reason: reason || null }),
        }
      );
      const body = await resp.json();
      if (!resp.ok || !body.ok) throw new Error(body.error ?? `HTTP ${resp.status}`);
      await load();
    } catch (e: any) {
      setErr(e.message ?? String(e));
    } finally {
      setWorking(null);
    }
  }

  if (!isManager) {
    return (
      <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
        Manager-only. Ask your manager to review canvasser submissions.
      </div>
    );
  }

  return (
    <>
      {/* Filter pills */}
      <div className="mb-4 flex flex-wrap gap-2">
        {(["pending","approved","rejected","all"] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              filter === f ? "bg-orange-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {err ? (
        <div className="mb-3 rounded-md bg-red-50 p-3 text-sm text-red-800">⚠ {err}</div>
      ) : null}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
          <div className="text-sm font-medium text-slate-700">No {filter !== "all" ? filter : ""} appointments.</div>
          <div className="mt-1 text-xs text-slate-500">When canvassers submit appointments from /canvas, they'll show up here.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <ApprovalCard
              key={row.id}
              row={row}
              working={working === row.id}
              onApprove={() => approve(row)}
              onReject={() => reject(row)}
            />
          ))}
        </div>
      )}
    </>
  );
}

// Standalone /canvas/approvals page — chrome + the shared inner queue.
export default function CanvasApprovals() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-100">
      <div className="border-b border-slate-200 bg-white px-4 py-3">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="rounded-md px-2 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-100">← Home</button>
            <div>
              <div className="text-base font-bold text-slate-900">Canvasser Approvals</div>
              <div className="text-xs text-slate-500">Review pending appointments and convert to deals.</div>
            </div>
          </div>
          <button onClick={() => navigate("/canvas")} className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white">🗺 Canvas →</button>
        </div>
      </div>

      <div className="mx-auto max-w-5xl p-4">
        <CanvasApprovalsInner />
      </div>
    </div>
  );
}

function ApprovalCard({
  row, working, onApprove, onReject,
}: {
  row: PendingRow; working: boolean; onApprove: () => void; onReject: () => void;
}) {
  const apptDate = new Date(row.appointment_at);
  const submittedAgo = relativeTime(row.submitted_at);
  const isPending = row.status === "pending";

  return (
    <div className={`rounded-lg border bg-white shadow-sm ${isPending ? "border-orange-200" : "border-slate-200 opacity-90"}`}>
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <StatusChip status={row.status} />
            {(() => {
              const ft = distanceFeet(
                row.submitter_lat, row.submitter_lon,
                row.pin?.lat ?? null, row.pin?.lon ?? null,
              );
              const chip = distanceChip(ft);
              if (!chip) return null;
              const titleText = ft != null
                ? `Rep was ${Math.round(ft)} ft from the pin when they submitted`
                : "GPS distance unknown";
              return (
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${chip.bg} ${chip.color}`}
                  title={titleText}
                >
                  {chip.emoji} {chip.label}
                </span>
              );
            })()}
            {/* Oct 27, 2026: "Log a lead" by typed address — no map pin. */}
            {!row.knock_pin_id ? (
              <span
                className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600"
                title="Logged by typing the address (no map pin), so there's no distance check"
              >
                ✍️ Logged by address
              </span>
            ) : null}
            <span className="text-slate-500">
              Submitted {submittedAgo} by <strong className="text-slate-700">{row.submitter?.full_name ?? "—"}</strong>
            </span>
          </div>
          {row.deal_id ? (
            <a href={`/deal/${row.deal_id}`} target="_blank" rel="noreferrer" className="font-semibold text-orange-600 hover:underline">Open deal →</a>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-3">
        <div>
          <Label>Homeowner</Label>
          <Value bold>{row.homeowner_first} {row.homeowner_last}</Value>
          <div className="mt-1 font-mono text-xs text-slate-600">{fmtPhone(row.homeowner_phone)}</div>
          {row.homeowner_email ? <div className="text-xs text-slate-600">{row.homeowner_email}</div> : null}
          {!row.sms_consent ? (
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700">⚠ No SMS consent</div>
          ) : null}
        </div>

        <div>
          <Label>Property</Label>
          <Value>{row.property_street}</Value>
          <div className="text-xs text-slate-600">
            {[row.property_city, row.property_state, row.property_zip].filter(Boolean).join(", ")}
          </div>
          {row.insurance_company ? (
            <div className="mt-1 text-xs"><span className="text-slate-500">Carrier: </span><strong className="text-slate-800">{row.insurance_company}</strong></div>
          ) : null}
          {row.roof_age_estimate ? (
            <div className="text-xs"><span className="text-slate-500">Est. roof age: </span><strong className="text-slate-800">{row.roof_age_estimate} yrs</strong></div>
          ) : null}
        </div>

        <div>
          <Label>Appointment</Label>
          <Value bold>
            {apptDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
          </Value>
          <div className="text-xs text-slate-600">
            {apptDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
          </div>
          {row.notes ? (
            <div className="mt-2 text-xs text-slate-700">
              <span className="text-slate-500">Notes: </span>{row.notes}
            </div>
          ) : null}
        </div>
      </div>

      {row.status === "rejected" && row.rejection_reason ? (
        <div className="border-t border-slate-100 bg-red-50 px-4 py-2 text-xs">
          <span className="font-semibold text-red-800">Rejected: </span>
          <span className="text-red-700">{row.rejection_reason}</span>
        </div>
      ) : null}

      {isPending ? (
        <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3">
          <button
            onClick={onApprove}
            disabled={working}
            className="rounded-md bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50"
          >
            {working ? "Working…" : "✅ Approve & create deal"}
          </button>
          <button
            onClick={onReject}
            disabled={working}
            className="rounded-md bg-white border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            Reject…
          </button>
        </div>
      ) : null}
    </div>
  );
}

function StatusChip({ status }: { status: string }) {
  const cls =
    status === "pending"  ? "bg-orange-100 text-orange-800" :
    status === "approved" ? "bg-emerald-100 text-emerald-800" :
    "bg-red-100 text-red-800";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>
      {status}
    </span>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{children}</div>;
}
function Value({ children, bold }: { children: React.ReactNode; bold?: boolean }) {
  return <div className={`mt-0.5 ${bold ? "text-base font-semibold text-slate-900" : "text-sm text-slate-800"}`}>{children}</div>;
}

function fmtPhone(s: string): string {
  const d = s.replace(/\D/g, "");
  if (d.length === 10) return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith("1")) return `${d.slice(1,4)}-${d.slice(4,7)}-${d.slice(7)}`;
  return s;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms/60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms/3_600_000)}h ago`;
  return `${Math.floor(ms/86_400_000)}d ago`;
}
